import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, Edit, Save, X, Plus, Trash2, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export function CustomerDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'DE'
  });

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  // Latest order address (for display)
  const [latestOrderAddress, setLatestOrderAddress] = useState(null);

  useEffect(() => {
    loadCustomerDetails();
  }, [id]);

  const loadCustomerDetails = async () => {
    try {
      setLoading(true);

      // Load customer data
      const customerResponse = await api.get(`/admin/customers/${id}`);
      if (customerResponse.success) {
        const customerData = customerResponse.customer;
        setCustomer(customerData);

        // Initialize edit form
        setEditForm({
          first_name: customerData.first_name || '',
          last_name: customerData.last_name || '',
          email: customerData.email || '',
          phone: customerData.phone || '',
          street: customerData.street || '',
          postal_code: customerData.postal_code || '',
          city: customerData.city || '',
          country: customerData.country || 'DE'
        });
      }

      // Load customer notes
      const notesResponse = await api.get(`/admin/customers/${id}/notes`);
      if (notesResponse.success) {
        setNotes(notesResponse.notes || []);
      }

      // Load customer orders
      const ordersResponse = await api.get(`/admin/customers/${id}/orders`);
      if (ordersResponse.success) {
        const customerOrders = ordersResponse.orders || [];
        setOrders(customerOrders);

        // Get latest order with shipping address
        const latestOrder = customerOrders.find(order =>
          order.shipping_address1 && order.shipping_zip && order.shipping_city
        );

        if (latestOrder) {
          setLatestOrderAddress({
            street: latestOrder.shipping_address1,
            street_number: latestOrder.shipping_address2 || '',
            postal_code: latestOrder.shipping_zip,
            city: latestOrder.shipping_city,
            country: latestOrder.shipping_country || 'Deutschland'
          });
        }
      }
    } catch (error) {
      console.error('Error loading customer details:', error);
      toast.error('Fehler beim Laden der Kundendaten');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.first_name || !editForm.last_name || !editForm.email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/admin/customers/${id}`, editForm);
      toast.success('Kundendaten erfolgreich aktualisiert');
      setEditing(false);
      await loadCustomerDetails();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Fehler beim Aktualisieren der Kundendaten');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    // Reset form to current customer data
    setEditForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      street: customer.street || '',
      postal_code: customer.postal_code || '',
      city: customer.city || '',
      country: customer.country || 'DE'
    });
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Bitte geben Sie eine Notiz ein');
      return;
    }

    try {
      setAddingNote(true);
      await api.post(`/admin/customers/${id}/notes`, { content: newNote });
      toast.success('Notiz erfolgreich hinzugefügt');
      setNewNote('');
      await loadCustomerDetails();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Fehler beim Hinzufügen der Notiz');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Möchten Sie diese Notiz wirklich löschen?')) {
      return;
    }

    try {
      await api.delete(`/admin/customers/${id}/notes/${noteId}`);
      toast.success('Notiz erfolgreich gelöscht');
      await loadCustomerDetails();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Fehler beim Löschen der Notiz');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-400 mt-4">Lade Kundendaten...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Kunde nicht gefunden</p>
        <Button variant="secondary" onClick={() => navigate('/customers')} className="mt-4">
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/customers')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {customer.first_name} {customer.last_name}
            </h1>
            <p className="text-gray-400 mt-1">Kunde seit {format(new Date(customer.created_at), 'dd. MMMM yyyy', { locale: de })}</p>
          </div>

          {!editing && (
            <Button onClick={() => setEditing(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Kontaktinformationen</CardTitle>
                {editing && (
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={saving}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Abbrechen
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {saving ? 'Speichern...' : 'Speichern'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {editing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vorname *
                        </label>
                        <input
                          type="text"
                          value={editForm.first_name}
                          onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nachname *
                        </label>
                        <input
                          type="text"
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">E-Mail</p>
                        <p className="text-gray-900">{customer.email}</p>
                      </div>
                    </div>

                    {customer.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Telefon</p>
                          <p className="text-gray-900">{customer.phone}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle>Adresse (aus letzter Bestellung)</CardTitle>
            </CardHeader>
            <CardBody>
              {editing ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Hinweis:</strong> Die Adresse wird jetzt in den Bestellungen gespeichert. Diese Felder dienen nur als Backup.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Straße & Hausnummer
                    </label>
                    <input
                      type="text"
                      value={editForm.street}
                      onChange={(e) => setEditForm({ ...editForm, street: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PLZ
                      </label>
                      <input
                        type="text"
                        value={editForm.postal_code}
                        onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stadt
                      </label>
                      <input
                        type="text"
                        value={editForm.city}
                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Land
                    </label>
                    <select
                      value={editForm.country}
                      onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="DE">Deutschland</option>
                      <option value="AT">Österreich</option>
                      <option value="CH">Schweiz</option>
                    </select>
                  </div>
                </div>
              ) : latestOrderAddress ? (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-900">
                      {latestOrderAddress.street} {latestOrderAddress.street_number}
                    </p>
                    <p className="text-gray-900">
                      {latestOrderAddress.postal_code} {latestOrderAddress.city}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">{latestOrderAddress.country}</p>
                    <p className="text-xs text-blue-600 mt-2">Aus letzter Bestellung</p>
                  </div>
                </div>
              ) : (customer.street || customer.city) ? (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    {customer.street && <p className="text-gray-900">{customer.street}</p>}
                    {(customer.postal_code || customer.city) && (
                      <p className="text-gray-900">
                        {customer.postal_code} {customer.city}
                      </p>
                    )}
                    {customer.country && <p className="text-gray-500 text-sm mt-1">{customer.country}</p>}
                    <p className="text-xs text-gray-500 mt-2">Backup-Adresse (Customer-Tabelle)</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">Keine Adresse hinterlegt</p>
              )}
            </CardBody>
          </Card>

          {/* Order History */}
          <Card>
            <CardHeader>
              <CardTitle>Bestellungen ({orders.length})</CardTitle>
            </CardHeader>
            <CardBody>
              {orders.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-4">Noch keine Bestellungen</p>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">Bestellung #{order.shopify_order_number || order.order_id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-500">
                            {format(new Date(order.shopify_created_at || order.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{order.total_amount}€</p>
                          <p className="text-sm text-gray-500">{order.status}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Notes Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notizen</CardTitle>
            </CardHeader>
            <CardBody>
              {/* Add Note */}
              <div className="mb-4">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Neue Notiz hinzufügen..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={3}
                />
                <Button
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  size="sm"
                  className="mt-2 w-full"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {addingNote ? 'Hinzufügen...' : 'Notiz hinzufügen'}
                </Button>
              </div>

              {/* Notes List */}
              <div className="space-y-3">
                {notes.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">Noch keine Notizen</p>
                ) : (
                  notes.map((note) => (
                    <div key={note.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm text-gray-900">{note.content}</p>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(note.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardBody>
          </Card>

          {/* Customer Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Statistik</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Gesamtumsatz</p>
                  <p className="text-2xl font-bold text-gray-900">{customer.total_revenue || 0}€</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Anzahl Bestellungen</p>
                  <p className="text-2xl font-bold text-gray-900">{customer.total_orders || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Kunde seit</p>
                  <p className="text-sm text-gray-900">
                    {format(new Date(customer.created_at), 'dd. MMMM yyyy', { locale: de })}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
